import { toLocalDateValue } from '@/helpers/doctorScheduleTimeHelper';

type ScheduleEntry = {
  date?: string | null;
  times?: unknown[];
  slots?: unknown[];
} & Record<string, unknown>;

type BookingOptionEntry = Record<string, unknown> & {
  key?: string | number | null;
  bookingTypeId?: string | number | null;
};

type CenterEntry = Record<string, unknown> & {
  key?: string | number | null;
  id?: string | number | null;
  name?: string | null;
};

type DoctorLike = Record<string, unknown> & {
  id?: string | number | null;
  bookingOptions?: unknown;
  centers?: unknown;
  searchCycleId?: string | number | null;
};

type DateWindow = {
  dates: Array<{ iso: string }>;
  firstDate: string;
  lastDate: string;
};

export function buildDoctorResetKey(doctor: DoctorLike | null): string {
  if (!doctor || typeof doctor !== 'object') {
    return 'empty';
  }

  const idPart = doctor.id !== undefined && doctor.id !== null ? String(doctor.id) : 'no-id';
  const bookingKeys = Array.isArray(doctor.bookingOptions)
    ? doctor.bookingOptions.map((optionRaw, index: number) => {
        if (!optionRaw || typeof optionRaw !== 'object') {
          return `opt-${index}`;
        }
        const option = optionRaw as BookingOptionEntry;
        if (option.key !== undefined && option.key !== null) {
          return String(option.key);
        }
        if (option.bookingTypeId !== undefined && option.bookingTypeId !== null) {
          return `booking-${option.bookingTypeId}`;
        }
        return `opt-${index}`;
      }).join('|')
    : 'no-options';

  const centerKeys = Array.isArray(doctor.centers)
    ? doctor.centers.map((centerRaw, index: number) => {
        if (!centerRaw || typeof centerRaw !== 'object') {
          return `center-${index}`;
        }
        const center = centerRaw as CenterEntry;
        if (center.key !== undefined && center.key !== null) {
          return String(center.key);
        }
        if (center.id !== undefined && center.id !== null) {
          return String(center.id);
        }
        if (center.name) {
          return `name-${center.name}`;
        }
        return `center-${index}`;
      }).join('|')
    : 'no-centers';

  const cycleKey = doctor.searchCycleId !== undefined && doctor.searchCycleId !== null
    ? `cycle-${doctor.searchCycleId}`
    : 'no-cycle';

  return `${idPart}|${bookingKeys}|${centerKeys}|${cycleKey}`;
}

export function buildFallbackDateWindow(baseDate: Date | string | null, currentDateGroupIndex: number): DateWindow | null {
  if (!baseDate) {
    return null;
  }
  const base = baseDate instanceof Date ? new Date(baseDate) : new Date(String(baseDate));
  if (Number.isNaN(base.getTime())) {
    return null;
  }
  base.setHours(0, 0, 0, 0);

  const start = new Date(base);
  const offset = currentDateGroupIndex * 4;
  start.setDate(start.getDate() + offset);

  const dates: Array<{ iso: string }> = [];
  for (let i = 0; i < 4; i += 1) {
    const nextDate = new Date(start);
    nextDate.setDate(start.getDate() + i);
    const iso = toLocalDateValue(nextDate);
    if (iso) {
      dates.push({ iso });
    }
  }

  if (!dates.length) {
    return null;
  }

  return {
    dates,
    firstDate: dates[0].iso,
    lastDate: dates[dates.length - 1].iso,
  };
}

export function buildCurrentDates(
  scheduleSource: ScheduleEntry[] | null | undefined,
  baseDate: Date | string | null,
  currentDateGroupIndex: number,
  formatDate: (iso: string) => Record<string, unknown>,
): Array<Record<string, unknown> & { iso: string }> {
  const schedule = Array.isArray(scheduleSource) ? scheduleSource : [];
  if (!schedule.length) {
    const fallback = buildFallbackDateWindow(baseDate, currentDateGroupIndex);
    if (!fallback) {
      return [];
    }
    return fallback.dates.map((item) => {
      const formatted = formatDate(item.iso);
      return {
        ...formatted,
        iso: item.iso,
      } as Record<string, unknown> & { iso: string };
    });
  }

  const start = currentDateGroupIndex * 4;
  const end = start + 4;
  const slice = schedule.slice(start, end);
  const dates = slice.map((item) => {
    const iso = item.date ? String(item.date) : '';
    const formatted = formatDate(iso);
    return {
      ...formatted,
      iso,
    } as Record<string, unknown> & { iso: string };
  });

  if (dates.length && dates.length < 4) {
    const lastIso = dates[dates.length - 1].iso;
    const cursor = lastIso ? new Date(`${lastIso}T00:00:00`) : null;
    while (cursor && dates.length < 4) {
      cursor.setDate(cursor.getDate() + 1);
      const iso = toLocalDateValue(cursor);
      if (!iso) {
        break;
      }
      const formatted = formatDate(iso);
      dates.push({
        ...formatted,
        iso,
      } as Record<string, unknown> & { iso: string });
    }
  }

  return dates;
}
